/**
 * Mirrors of the Prisma enums, expressed as plain const objects so that web,
 * native and Node clients can import them without pulling in @prisma/client.
 * Keep in sync with the Prisma schema. The test that enforces that lives in
 * the backend repo now (src/common/constants/enums.drift.spec.ts) -- after the
 * split, that is the only place both the schema and this package are in scope.
 */

export const TableState = {
  AVAILABLE: 'AVAILABLE',
  OCCUPIED: 'OCCUPIED',
  PAYMENT_PENDING: 'PAYMENT_PENDING',
  CLEANING: 'CLEANING',
  RESERVED: 'RESERVED',
  BLOCKED: 'BLOCKED',
  OUT_OF_SERVICE: 'OUT_OF_SERVICE',
} as const;
export type TableState = (typeof TableState)[keyof typeof TableState];

export const AreaType = {
  INDOOR: 'INDOOR',
  OUTDOOR: 'OUTDOOR',
  VIP: 'VIP',
  SMOKING: 'SMOKING',
  FAMILY: 'FAMILY',
  KIDS: 'KIDS',
  GAMING: 'GAMING',
  ROOFTOP: 'ROOFTOP',
  TERRACE: 'TERRACE',
  PRIVATE_ROOM: 'PRIVATE_ROOM',
  OTHER: 'OTHER',
} as const;
export type AreaType = (typeof AreaType)[keyof typeof AreaType];

export const MapElementType = {
  TABLE: 'TABLE',
  RESOURCE: 'RESOURCE',
  WALL: 'WALL',
  DOOR: 'DOOR',
  WINDOW: 'WINDOW',
  BAR: 'BAR',
  KITCHEN: 'KITCHEN',
  CASHIER: 'CASHIER',
  BATHROOM: 'BATHROOM',
  ENTRANCE: 'ENTRANCE',
  STAIRS: 'STAIRS',
  TEXT: 'TEXT',
  CUSTOM: 'CUSTOM',
} as const;
export type MapElementType = (typeof MapElementType)[keyof typeof MapElementType];

export const ResourceType = {
  PLAYSTATION: 'PLAYSTATION',
  BILLIARDS: 'BILLIARDS',
  KIDS_AREA: 'KIDS_AREA',
  KARAOKE_ROOM: 'KARAOKE_ROOM',
  PRIVATE_ROOM: 'PRIVATE_ROOM',
  MEETING_ROOM: 'MEETING_ROOM',
  OTHER: 'OTHER',
} as const;
export type ResourceType = (typeof ResourceType)[keyof typeof ResourceType];

export const ResourceStatus = {
  AVAILABLE: 'AVAILABLE',
  RESERVED: 'RESERVED',
  IN_USE: 'IN_USE',
  PAUSED: 'PAUSED',
  MAINTENANCE: 'MAINTENANCE',
  OUT_OF_SERVICE: 'OUT_OF_SERVICE',
} as const;
export type ResourceStatus = (typeof ResourceStatus)[keyof typeof ResourceStatus];

export const ResourceSessionStatus = {
  SCHEDULED: 'SCHEDULED',
  ACTIVE: 'ACTIVE',
  PAUSED: 'PAUSED',
  ENDED: 'ENDED',
  CANCELLED: 'CANCELLED',
} as const;
export type ResourceSessionStatus =
  (typeof ResourceSessionStatus)[keyof typeof ResourceSessionStatus];

export const FulfillmentType = {
  DINE_IN: 'DINE_IN',
  TAKEAWAY: 'TAKEAWAY',
  DELIVERY: 'DELIVERY',
} as const;
export type FulfillmentType = (typeof FulfillmentType)[keyof typeof FulfillmentType];

export const OrderStatus = {
  DRAFT: 'DRAFT',
  CONFIRMED: 'CONFIRMED',
  SENT_TO_KITCHEN: 'SENT_TO_KITCHEN',
  PREPARING: 'PREPARING',
  READY: 'READY',
  SERVED: 'SERVED',
  COMPLETED: 'COMPLETED',
  CANCELLED: 'CANCELLED',
  VOIDED: 'VOIDED',
  REFUNDED: 'REFUNDED',
} as const;
export type OrderStatus = (typeof OrderStatus)[keyof typeof OrderStatus];

/** §15 — what the guest sees; a projection of the internal states above. */
export const CustomerOrderStatus = {
  ORDER_RECEIVED: 'ORDER_RECEIVED',
  CONFIRMED: 'CONFIRMED',
  PREPARING: 'PREPARING',
  PARTIALLY_READY: 'PARTIALLY_READY',
  READY: 'READY',
  SERVED: 'SERVED',
  PICKED_UP: 'PICKED_UP',
  COMPLETED: 'COMPLETED',
} as const;
export type CustomerOrderStatus =
  (typeof CustomerOrderStatus)[keyof typeof CustomerOrderStatus];

export const TakeawayStatus = {
  DRAFT: 'DRAFT',
  SCHEDULED: 'SCHEDULED',
  RELEASED: 'RELEASED',
  PREPARING: 'PREPARING',
  READY: 'READY',
  HANDED_OVER: 'HANDED_OVER',
  COMPLETED: 'COMPLETED',
  CANCELLED: 'CANCELLED',
  REFUNDED: 'REFUNDED',
} as const;
export type TakeawayStatus = (typeof TakeawayStatus)[keyof typeof TakeawayStatus];

export const ReleaseStrategy = {
  IMMEDIATE: 'IMMEDIATE',
  SCHEDULED: 'SCHEDULED',
  ON_CUSTOMER_REQUEST: 'ON_CUSTOMER_REQUEST',
} as const;
export type ReleaseStrategy = (typeof ReleaseStrategy)[keyof typeof ReleaseStrategy];

export const OrderItemStatus = {
  PENDING: 'PENDING',
  QUEUED: 'QUEUED',
  PREPARING: 'PREPARING',
  READY: 'READY',
  AWAITING_PICKUP: 'AWAITING_PICKUP',
  PICKED_UP: 'PICKED_UP',
  SERVED: 'SERVED',
  CANCELLED: 'CANCELLED',
} as const;
export type OrderItemStatus = (typeof OrderItemStatus)[keyof typeof OrderItemStatus];

export const CheckStatus = {
  OPEN: 'OPEN',
  PARTIALLY_PAID: 'PARTIALLY_PAID',
  PAID: 'PAID',
  CLOSED: 'CLOSED',
  VOIDED: 'VOIDED',
  REFUNDED: 'REFUNDED',
} as const;
export type CheckStatus = (typeof CheckStatus)[keyof typeof CheckStatus];

export const PaymentStatus = {
  PENDING: 'PENDING',
  AUTHORIZED: 'AUTHORIZED',
  PAID: 'PAID',
  FAILED: 'FAILED',
  PARTIALLY_REFUNDED: 'PARTIALLY_REFUNDED',
  REFUNDED: 'REFUNDED',
} as const;
export type PaymentStatus = (typeof PaymentStatus)[keyof typeof PaymentStatus];

export const PaymentMethod = {
  CASH: 'CASH',
  CARD: 'CARD',
  WALLET: 'WALLET',
  BANK_TRANSFER: 'BANK_TRANSFER',
  VOUCHER: 'VOUCHER',
  LOYALTY_POINTS: 'LOYALTY_POINTS',
  OTHER: 'OTHER',
} as const;
export type PaymentMethod = (typeof PaymentMethod)[keyof typeof PaymentMethod];

export const ChargeType = {
  PRODUCT: 'PRODUCT',
  MEAL: 'MEAL',
  SERVICE: 'SERVICE',
  TIME: 'TIME',
  FEE: 'FEE',
  DISCOUNT: 'DISCOUNT',
  ADJUSTMENT: 'ADJUSTMENT',
  TIP: 'TIP',
  TAX: 'TAX',
} as const;
export type ChargeType = (typeof ChargeType)[keyof typeof ChargeType];

export const ServiceRequestType = {
  CALL_WAITER: 'CALL_WAITER',
  REQUEST_BILL: 'REQUEST_BILL',
  WATER: 'WATER',
  NAPKINS: 'NAPKINS',
  PLATES: 'PLATES',
  CLEANING: 'CLEANING',
  CUSTOM: 'CUSTOM',
} as const;
export type ServiceRequestType = (typeof ServiceRequestType)[keyof typeof ServiceRequestType];

export const ServiceRequestStatus = {
  CREATED: 'CREATED',
  ACCEPTED: 'ACCEPTED',
  DONE: 'DONE',
  CANCELLED: 'CANCELLED',
  ESCALATED: 'ESCALATED',
} as const;
export type ServiceRequestStatus =
  (typeof ServiceRequestStatus)[keyof typeof ServiceRequestStatus];

/**
 * ADR-0023 §3.1 — `BRAND` inserted between `TENANT` and `BRANCH`. A brand
 * never leaves its tenant (a franchisor's `RoleAssignment` sits at
 * `scopeLevel: BRAND, scopeId: <brand id>`), so this is one more rung on the
 * existing ladder, not a second hierarchy. `Hooks/useRequireScope.ts`'s own
 * `SCOPE_HIERARCHY` mirrors this list and MUST carry `BRAND` in the same
 * position — see that file for why a missing rung is a silent authorization
 * bug, not just a missing label.
 */
export const ScopeLevel = {
  PLATFORM: 'PLATFORM',
  TENANT: 'TENANT',
  BRAND: 'BRAND',
  BRANCH: 'BRANCH',
  FLOOR: 'FLOOR',
  AREA: 'AREA',
  TABLE: 'TABLE',
  RESOURCE: 'RESOURCE',
} as const;
export type ScopeLevel = (typeof ScopeLevel)[keyof typeof ScopeLevel];

/** ADR-0023 §7 — which revenue figure a royalty scheme's rate is applied to. */
export const RoyaltyBasis = {
  GROSS_SALES: 'GROSS_SALES',
  NET_SALES: 'NET_SALES',
} as const;
export type RoyaltyBasis = (typeof RoyaltyBasis)[keyof typeof RoyaltyBasis];

/** ADR-0023 §7 — every generated statement is DRAFT; no route moves it to FINAL yet. */
export const RoyaltyStatementStatus = {
  DRAFT: 'DRAFT',
  FINAL: 'FINAL',
} as const;
export type RoyaltyStatementStatus =
  (typeof RoyaltyStatementStatus)[keyof typeof RoyaltyStatementStatus];

export const CatalogItemType = {
  PRODUCT: 'PRODUCT',
  MEAL_COMBO: 'MEAL_COMBO',
  SERVICE: 'SERVICE',
  TIME_BASED_SERVICE: 'TIME_BASED_SERVICE',
  EXPERIENCE: 'EXPERIENCE',
  PACKAGE: 'PACKAGE',
  ADD_ON: 'ADD_ON',
} as const;
export type CatalogItemType = (typeof CatalogItemType)[keyof typeof CatalogItemType];

export const SubscriptionStatus = {
  TRIAL: 'TRIAL',
  ACTIVE: 'ACTIVE',
  PAST_DUE: 'PAST_DUE',
  CANCELLED: 'CANCELLED',
  EXPIRED: 'EXPIRED',
} as const;
export type SubscriptionStatus = (typeof SubscriptionStatus)[keyof typeof SubscriptionStatus];

export const ReservationStatus = {
  PENDING: 'PENDING',
  CONFIRMED: 'CONFIRMED',
  SEATED: 'SEATED',
  NO_SHOW: 'NO_SHOW',
  CANCELLED: 'CANCELLED',
  COMPLETED: 'COMPLETED',
} as const;
export type ReservationStatus = (typeof ReservationStatus)[keyof typeof ReservationStatus];

export const PurchaseOrderStatus = {
  DRAFT: 'DRAFT',
  SUBMITTED: 'SUBMITTED',
  APPROVED: 'APPROVED',
  PARTIALLY_RECEIVED: 'PARTIALLY_RECEIVED',
  RECEIVED: 'RECEIVED',
  CANCELLED: 'CANCELLED',
  CLOSED: 'CLOSED',
} as const;
export type PurchaseOrderStatus = (typeof PurchaseOrderStatus)[keyof typeof PurchaseOrderStatus];

/* ------------------------------------------------------------------ *

/**
 * X-12 — enums that lived only in `schema.prisma` while a client
 * hand-mirrored them. Every one below replaced a local union or
 * `as const` array in dashboard / platform-admin / a mobile app, so the
 * names now have exactly one definition and cannot drift silently.
 * ------------------------------------------------------------------ */

export const SessionStatus = {
  OPEN: 'OPEN',
  CLOSED: 'CLOSED',
  CANCELLED: 'CANCELLED',
} as const;
export type SessionStatus = (typeof SessionStatus)[keyof typeof SessionStatus];

export const PricingUnit = {
  PER_MINUTE: 'PER_MINUTE',
  PER_30_MINUTES: 'PER_30_MINUTES',
  PER_HOUR: 'PER_HOUR',
  FIXED_DURATION: 'FIXED_DURATION',
  FIXED_PRICE: 'FIXED_PRICE',
} as const;
export type PricingUnit = (typeof PricingUnit)[keyof typeof PricingUnit];

/** The five §24.5 policy gates. */
export const PolicyKind = {
  DISCOUNT: 'DISCOUNT',
  VOID: 'VOID',
  REFUND: 'REFUND',
  PRICE_OVERRIDE: 'PRICE_OVERRIDE',
  STOCK_ADJUSTMENT: 'STOCK_ADJUSTMENT',
} as const;
export type PolicyKind = (typeof PolicyKind)[keyof typeof PolicyKind];

export const ApprovalStatus = {
  PENDING: 'PENDING',
  APPROVED: 'APPROVED',
  REJECTED: 'REJECTED',
  EXPIRED: 'EXPIRED',
} as const;
export type ApprovalStatus = (typeof ApprovalStatus)[keyof typeof ApprovalStatus];

export const KitchenTicketStatus = {
  NEW: 'NEW',
  PREPARING: 'PREPARING',
  READY: 'READY',
  /**
   * The guest has it. Every non-cancelled line reached SERVED or PICKED_UP, so
   * the pass is finished with the ticket and the board stops showing it. READY
   * only ever meant "on the pass", which is why an uncollected takeaway bag sat
   * in the pickup queue for days.
   *
   * Placed after READY on purpose: `enums.drift.spec.ts` in the backend
   * compares member ORDER against the Prisma enum, not just membership.
   */
  COLLECTED: 'COLLECTED',
  RECALLED: 'RECALLED',
  CANCELLED: 'CANCELLED',
} as const;
export type KitchenTicketStatus = (typeof KitchenTicketStatus)[keyof typeof KitchenTicketStatus];

export const DeviceType = {
  POS: 'POS',
  KDS: 'KDS',
  WAITER_TABLET: 'WAITER_TABLET',
  CUSTOMER_KIOSK: 'CUSTOMER_KIOSK',
  PRINTER: 'PRINTER',
  /**
   * ADR-0009 — the agent inside the venue that owns the printers. It is a
   * device like any other (a branch, a version, a last seen), so it belongs in
   * one fleet list rather than a second registry beside it.
   */
  PRINT_BRIDGE: 'PRINT_BRIDGE',
  /**
   * A card terminal, registrable for inventory, version and health only.
   * Everything transactional about it belongs to the payments epic.
   */
  PAYMENT_TERMINAL: 'PAYMENT_TERMINAL',
  OTHER: 'OTHER',
} as const;
export type DeviceType = (typeof DeviceType)[keyof typeof DeviceType];

export const EntitlementType = {
  CATALOG_ITEM: 'CATALOG_ITEM',
  RESOURCE_TIME: 'RESOURCE_TIME',
} as const;
export type EntitlementType = (typeof EntitlementType)[keyof typeof EntitlementType];

export const EntitlementStatus = {
  TEMPLATE: 'TEMPLATE',
  ISSUED: 'ISSUED',
  PARTIALLY_CONSUMED: 'PARTIALLY_CONSUMED',
  CONSUMED: 'CONSUMED',
  EXPIRED: 'EXPIRED',
  CANCELLED: 'CANCELLED',
} as const;
export type EntitlementStatus = (typeof EntitlementStatus)[keyof typeof EntitlementStatus];

export const UnitType = {
  WEIGHT: 'WEIGHT',
  VOLUME: 'VOLUME',
  COUNT: 'COUNT',
  LENGTH: 'LENGTH',
  TIME: 'TIME',
} as const;
export type UnitType = (typeof UnitType)[keyof typeof UnitType];

export const InventoryItemType = {
  RAW_MATERIAL: 'RAW_MATERIAL',
  FINISHED_GOOD: 'FINISHED_GOOD',
  PACKAGING: 'PACKAGING',
  CONSUMABLE: 'CONSUMABLE',
} as const;
export type InventoryItemType = (typeof InventoryItemType)[keyof typeof InventoryItemType];

export const StockMovementType = {
  PURCHASE_RECEIPT: 'PURCHASE_RECEIPT',
  SALE_CONSUMPTION: 'SALE_CONSUMPTION',
  WASTE: 'WASTE',
  TRANSFER_IN: 'TRANSFER_IN',
  TRANSFER_OUT: 'TRANSFER_OUT',
  ADJUSTMENT: 'ADJUSTMENT',
  STOCK_COUNT: 'STOCK_COUNT',
  SUPPLIER_RETURN: 'SUPPLIER_RETURN',
  PRODUCTION: 'PRODUCTION',
} as const;
export type StockMovementType = (typeof StockMovementType)[keyof typeof StockMovementType];

export const RecipeVersionStatus = {
  DRAFT: 'DRAFT',
  ACTIVE: 'ACTIVE',
  ARCHIVED: 'ARCHIVED',
} as const;
export type RecipeVersionStatus = (typeof RecipeVersionStatus)[keyof typeof RecipeVersionStatus];

export const PurchaseReceiptStatus = {
  DRAFT: 'DRAFT',
  RECEIVED: 'RECEIVED',
  PARTIALLY_RETURNED: 'PARTIALLY_RETURNED',
  RETURNED: 'RETURNED',
  CANCELLED: 'CANCELLED',
} as const;
export type PurchaseReceiptStatus =
  (typeof PurchaseReceiptStatus)[keyof typeof PurchaseReceiptStatus];

export const FeedbackStatus = {
  NEW: 'NEW',
  ACKNOWLEDGED: 'ACKNOWLEDGED',
  IN_RECOVERY: 'IN_RECOVERY',
  RESOLVED: 'RESOLVED',
  DISMISSED: 'DISMISSED',
} as const;
export type FeedbackStatus = (typeof FeedbackStatus)[keyof typeof FeedbackStatus];

export const PromotionType = {
  PERCENTAGE: 'PERCENTAGE',
  FIXED_AMOUNT: 'FIXED_AMOUNT',
  BUY_X_GET_Y: 'BUY_X_GET_Y',
  BUNDLE_PRICE: 'BUNDLE_PRICE',
  FREE_ITEM: 'FREE_ITEM',
  HAPPY_HOUR: 'HAPPY_HOUR',
} as const;
export type PromotionType = (typeof PromotionType)[keyof typeof PromotionType];

export const CouponStatus = {
  ACTIVE: 'ACTIVE',
  REDEEMED: 'REDEEMED',
  EXPIRED: 'EXPIRED',
  DISABLED: 'DISABLED',
} as const;
export type CouponStatus = (typeof CouponStatus)[keyof typeof CouponStatus];

// ---------------------------------------------------------------------------
// §11 / ADR-0009 — device, printer and hardware fleet
// ---------------------------------------------------------------------------

export const VersionEnforcement = {
  /** Below the floor is reported and nothing else. */
  WARN: 'WARN',
  /** Below the floor is refused a heartbeat, with a stable error code. */
  BLOCK: 'BLOCK',
} as const;
export type VersionEnforcement = (typeof VersionEnforcement)[keyof typeof VersionEnforcement];

/**
 * How a job physically reaches the paper.
 *
 * Every `BRIDGE_*` kind means "the server cannot dial this printer" — only the
 * agent inside the venue can. `CLOUD_HTTP` is the vendor endpoint the server
 * genuinely can reach; it is a seam, not a second delivery mechanism, and its
 * jobs live in the same spool with the same statuses.
 */
export const PrinterConnection = {
  BRIDGE_LAN: 'BRIDGE_LAN',
  BRIDGE_USB: 'BRIDGE_USB',
  BRIDGE_BLUETOOTH: 'BRIDGE_BLUETOOTH',
  CLOUD_HTTP: 'CLOUD_HTTP',
} as const;
export type PrinterConnection = (typeof PrinterConnection)[keyof typeof PrinterConnection];

export const PrinterProtocol = {
  ESC_POS: 'ESC_POS',
  STAR_LINE: 'STAR_LINE',
  ZPL: 'ZPL',
  PDF: 'PDF',
} as const;
export type PrinterProtocol = (typeof PrinterProtocol)[keyof typeof PrinterProtocol];

/**
 * What the hardware last said about itself. `UNKNOWN` is not a failure — it is
 * a printer nothing has reported on yet, and collapsing it into `OFFLINE` would
 * put a brand new printer on the alert list.
 */
export const PrinterStatus = {
  UNKNOWN: 'UNKNOWN',
  ONLINE: 'ONLINE',
  OUT_OF_PAPER: 'OUT_OF_PAPER',
  COVER_OPEN: 'COVER_OPEN',
  JAMMED: 'JAMMED',
  OFFLINE: 'OFFLINE',
  ERROR: 'ERROR',
} as const;
export type PrinterStatus = (typeof PrinterStatus)[keyof typeof PrinterStatus];

/** What a destination is FOR, and the fallback when no routing rule matches. */
export const PrintDestinationRole = {
  KITCHEN: 'KITCHEN',
  RECEIPT: 'RECEIPT',
  BAR: 'BAR',
  LABEL: 'LABEL',
  REPORT: 'REPORT',
} as const;
export type PrintDestinationRole =
  (typeof PrintDestinationRole)[keyof typeof PrintDestinationRole];

export const PrintDocumentType = {
  KITCHEN_TICKET: 'KITCHEN_TICKET',
  BAR_TICKET: 'BAR_TICKET',
  RECEIPT: 'RECEIPT',
  BILL: 'BILL',
  LABEL: 'LABEL',
  TEST_PAGE: 'TEST_PAGE',
  SHIFT_REPORT: 'SHIFT_REPORT',
} as const;
export type PrintDocumentType = (typeof PrintDocumentType)[keyof typeof PrintDocumentType];

/**
 * `DEAD` is out of attempts: nobody is going to retry it without a person.
 * That is the number on this screen that should make somebody move.
 */
export const PrintJobStatus = {
  QUEUED: 'QUEUED',
  CLAIMED: 'CLAIMED',
  PRINTED: 'PRINTED',
  FAILED: 'FAILED',
  DEAD: 'DEAD',
  CANCELLED: 'CANCELLED',
} as const;
export type PrintJobStatus = (typeof PrintJobStatus)[keyof typeof PrintJobStatus];

/**
 * `LATE` is not a failure of the print — it is an ack that arrived after its
 * lease was reclaimed. Recorded rather than dropped, because it is the only
 * evidence an operator has that a second copy may exist.
 */
export const PrintAttemptOutcome = {
  CLAIMED: 'CLAIMED',
  PRINTED: 'PRINTED',
  FAILED: 'FAILED',
  EXPIRED: 'EXPIRED',
  CANCELLED: 'CANCELLED',
  LATE: 'LATE',
} as const;
export type PrintAttemptOutcome = (typeof PrintAttemptOutcome)[keyof typeof PrintAttemptOutcome];
/**
 * §20 — central menu governance (ADR-0011). Three of these are LIVE to price
 * resolution — SCHEDULED, PUBLISHED and ROLLED_BACK — because `effectiveFrom`,
 * not this column, is what a branch actually charges from. ROLLED_BACK stays
 * live because the wrong prices really were charged while they stood, and the
 * checks written in that window still have to reconcile against them.
 */
export const MenuVersionStatus = {
  DRAFT: 'DRAFT',
  IN_REVIEW: 'IN_REVIEW',
  APPROVED: 'APPROVED',
  SCHEDULED: 'SCHEDULED',
  PUBLISHED: 'PUBLISHED',
  REJECTED: 'REJECTED',
  CANCELLED: 'CANCELLED',
  ROLLED_BACK: 'ROLLED_BACK',
} as const;
export type MenuVersionStatus = (typeof MenuVersionStatus)[keyof typeof MenuVersionStatus];

/**
 * A `BranchMenuRule.channels` member. Empty on a rule means every channel;
 * `ONLINE` is an order *origin*, not a fulfilment, and is unreachable from an
 * `Order` until a storefront epic can name itself (ADR-0011 §6).
 */
export const MenuChannel = {
  DINE_IN: 'DINE_IN',
  TAKEAWAY: 'TAKEAWAY',
  DELIVERY: 'DELIVERY',
  ONLINE: 'ONLINE',
} as const;
export type MenuChannel = (typeof MenuChannel)[keyof typeof MenuChannel];
/** ADR-0012 — a gift card is a bearer instrument; store credit is always named to a customer. */
export const WalletAccountKind = {
  GIFT_CARD: 'GIFT_CARD',
  STORE_CREDIT: 'STORE_CREDIT',
} as const;
export type WalletAccountKind = (typeof WalletAccountKind)[keyof typeof WalletAccountKind];

export const WalletAccountStatus = {
  /** Issued but not yet sold — a stolen blank is worth nothing until activated. */
  INACTIVE: 'INACTIVE',
  ACTIVE: 'ACTIVE',
  /** Frozen pending a fraud review. Reads still work. */
  SUSPENDED: 'SUSPENDED',
  /** Past `expiresAt`; the remaining value was written off by an EXPIRY line. */
  EXPIRED: 'EXPIRED',
  /** Fully spent and closed, or closed by staff. Terminal. */
  CLOSED: 'CLOSED',
} as const;
export type WalletAccountStatus = (typeof WalletAccountStatus)[keyof typeof WalletAccountStatus];

/** ADR-0012 — why one `WalletTransaction` line exists. The sign is authoritative; this is the label. */
export const WalletTransactionType = {
  ISSUE: 'ISSUE',
  RELOAD: 'RELOAD',
  REDEEM: 'REDEEM',
  /** Compensating entry reversing a REDEEM. Always positive, carries `reversesId`. */
  REFUND: 'REFUND',
  /** Compensating entry reversing an ISSUE or a RELOAD. Always negative, carries `reversesId`. */
  VOID: 'VOID',
  /** Signed manual correction with a mandatory reason. */
  ADJUSTMENT: 'ADJUSTMENT',
  /** Balance written off on expiry. Always negative. */
  EXPIRY: 'EXPIRY',
} as const;
export type WalletTransactionType = (typeof WalletTransactionType)[keyof typeof WalletTransactionType];

// ---------------------------------------------------------------------------
// §2 — the integration substrate (ADR-0002). `src/modules/integrations`.
// ---------------------------------------------------------------------------

export const IntegrationCategory = {
  PAYMENTS: 'PAYMENTS',
  ACCOUNTING: 'ACCOUNTING',
  DELIVERY: 'DELIVERY',
  MESSAGING: 'MESSAGING',
  FISCAL: 'FISCAL',
  OTHER: 'OTHER',
} as const;
export type IntegrationCategory = (typeof IntegrationCategory)[keyof typeof IntegrationCategory];

/** How a tenant proves who it is TO the provider. `NONE` is a real answer — an inbound-only connector has no outbound credential to hold. */
export const IntegrationAuthKind = {
  API_KEY: 'API_KEY',
  OAUTH2: 'OAUTH2',
  NONE: 'NONE',
} as const;
export type IntegrationAuthKind = (typeof IntegrationAuthKind)[keyof typeof IntegrationAuthKind];

/** PENDING is not a failure — an install exists before its credentials do. */
export const IntegrationInstallationStatus = {
  PENDING: 'PENDING',
  ACTIVE: 'ACTIVE',
  SUSPENDED: 'SUSPENDED',
  UNINSTALLED: 'UNINSTALLED',
} as const;
export type IntegrationInstallationStatus =
  (typeof IntegrationInstallationStatus)[keyof typeof IntegrationInstallationStatus];

/** Derived from recent delivery outcomes, never set by hand. UNKNOWN means "no traffic yet", distinct from HEALTHY. */
export const IntegrationHealth = {
  UNKNOWN: 'UNKNOWN',
  HEALTHY: 'HEALTHY',
  DEGRADED: 'DEGRADED',
  FAILING: 'FAILING',
} as const;
export type IntegrationHealth = (typeof IntegrationHealth)[keyof typeof IntegrationHealth];

export const IntegrationCredentialKind = {
  API_KEY: 'API_KEY',
  API_SECRET: 'API_SECRET',
  OAUTH_ACCESS_TOKEN: 'OAUTH_ACCESS_TOKEN',
  OAUTH_REFRESH_TOKEN: 'OAUTH_REFRESH_TOKEN',
  WEBHOOK_SIGNING_SECRET: 'WEBHOOK_SIGNING_SECRET',
} as const;
export type IntegrationCredentialKind =
  (typeof IntegrationCredentialKind)[keyof typeof IntegrationCredentialKind];

export const IntegrationDeliveryDirection = {
  INBOUND: 'INBOUND',
  OUTBOUND: 'OUTBOUND',
} as const;
export type IntegrationDeliveryDirection =
  (typeof IntegrationDeliveryDirection)[keyof typeof IntegrationDeliveryDirection];

/** DEAD_LETTERED is a status, not a second table (ADR-0002) — a dead letter is the delivery that failed. */
export const IntegrationDeliveryStatus = {
  PENDING: 'PENDING',
  DELIVERING: 'DELIVERING',
  DELIVERED: 'DELIVERED',
  FAILED: 'FAILED',
  DEAD_LETTERED: 'DEAD_LETTERED',
  CANCELLED: 'CANCELLED',
} as const;
export type IntegrationDeliveryStatus =
  (typeof IntegrationDeliveryStatus)[keyof typeof IntegrationDeliveryStatus];

export const IntegrationJobKind = {
  BACKFILL: 'BACKFILL',
  SYNC: 'SYNC',
  RECONCILE: 'RECONCILE',
  REPLAY: 'REPLAY',
} as const;
export type IntegrationJobKind = (typeof IntegrationJobKind)[keyof typeof IntegrationJobKind];

export const IntegrationJobStatus = {
  QUEUED: 'QUEUED',
  RUNNING: 'RUNNING',
  SUCCEEDED: 'SUCCEEDED',
  FAILED: 'FAILED',
  CANCELLED: 'CANCELLED',
} as const;
export type IntegrationJobStatus = (typeof IntegrationJobStatus)[keyof typeof IntegrationJobStatus];

// ---------------------------------------------------------------------------
// §3 — outbound webhooks (ADR-0004). `src/modules/webhooks`.
// ---------------------------------------------------------------------------

export const WebhookEndpointStatus = {
  ACTIVE: 'ACTIVE',
  /** Switched off from the dashboard, on purpose. */
  DISABLED: 'DISABLED',
  /** Switched off automatically after five consecutive dead-lettered deliveries. */
  DISABLED_FAILING: 'DISABLED_FAILING',
} as const;
export type WebhookEndpointStatus = (typeof WebhookEndpointStatus)[keyof typeof WebhookEndpointStatus];

/** PENDING -> DELIVERING -> SUCCEEDED, or back to PENDING with a later `nextAttemptAt`, and finally DEAD_LETTERED. */
export const WebhookDeliveryStatus = {
  PENDING: 'PENDING',
  DELIVERING: 'DELIVERING',
  SUCCEEDED: 'SUCCEEDED',
  FAILED: 'FAILED',
  DEAD_LETTERED: 'DEAD_LETTERED',
  /** The endpoint was deleted or disabled before this delivery was attempted. Never retried. */
  CANCELLED: 'CANCELLED',
} as const;
export type WebhookDeliveryStatus = (typeof WebhookDeliveryStatus)[keyof typeof WebhookDeliveryStatus];

// ---------------------------------------------------------------------------
// §9 / ADR-0010 — CRM: consent, duplicate merge and segmentation.
// `src/modules/customers/crm`, `src/modules/segments`.
// ---------------------------------------------------------------------------

/** The four channels the platform can reach a customer on. */
export const ContactChannel = {
  SMS: 'SMS',
  WHATSAPP: 'WHATSAPP',
  EMAIL: 'EMAIL',
  PUSH: 'PUSH',
} as const;
export type ContactChannel = (typeof ContactChannel)[keyof typeof ContactChannel];

/**
 * The absence of a row is not on this list — it reads as not-contactable
 * everywhere consent is checked, and the dashboard must not draw it as a
 * fourth status. See `ChannelConsent.status: null` in `Pages/CRM/lib.ts`.
 */
export const ConsentStatus = {
  GRANTED: 'GRANTED',
  DENIED: 'DENIED',
  WITHDRAWN: 'WITHDRAWN',
} as const;
export type ConsentStatus = (typeof ConsentStatus)[keyof typeof ConsentStatus];

/**
 * How the venue came to know a consent decision. `MERGE` and `ERASURE` are
 * system-written provenance and are never offered on the "record consent"
 * form — only the four human sources are.
 */
export const PersonalDataSource = {
  STAFF_ENTRY: 'STAFF_ENTRY',
  GUEST_SELF_SERVICE: 'GUEST_SELF_SERVICE',
  ONLINE_ORDER: 'ONLINE_ORDER',
  IMPORT: 'IMPORT',
  MERGE: 'MERGE',
  ERASURE: 'ERASURE',
} as const;
export type PersonalDataSource = (typeof PersonalDataSource)[keyof typeof PersonalDataSource];

/** What a human may pick when recording consent. `MERGE` / `ERASURE` are system-only. */
export const HUMAN_CONSENT_SOURCES = [
  PersonalDataSource.STAFF_ENTRY,
  PersonalDataSource.GUEST_SELF_SERVICE,
  PersonalDataSource.ONLINE_ORDER,
  PersonalDataSource.IMPORT,
] as const;

export const CustomerMergeStatus = {
  COMPLETED: 'COMPLETED',
  REVERSED: 'REVERSED',
} as const;
export type CustomerMergeStatus = (typeof CustomerMergeStatus)[keyof typeof CustomerMergeStatus];

export const SegmentRefreshMode = {
  MANUAL: 'MANUAL',
  SCHEDULED: 'SCHEDULED',
} as const;
export type SegmentRefreshMode = (typeof SegmentRefreshMode)[keyof typeof SegmentRefreshMode];

/** `Segment.definition.match` — every rule must hold, or at least one must. */
export const SegmentMatch = {
  ALL: 'ALL',
  ANY: 'ANY',
} as const;
export type SegmentMatch = (typeof SegmentMatch)[keyof typeof SegmentMatch];

/**
 * The closed field vocabulary a segment rule may name. Kept here as a type
 * even though `GET /segments/vocabulary` is what actually drives the rule
 * editor (never hardcode the field list itself) — this is only for switches
 * that need to be exhaustive over the shape a rule can take.
 */
export const SegmentField = {
  RECENCY_DAYS: 'RECENCY_DAYS',
  VISIT_COUNT: 'VISIT_COUNT',
  TOTAL_SPEND: 'TOTAL_SPEND',
  AVERAGE_SPEND: 'AVERAGE_SPEND',
  BRANCH: 'BRANCH',
  ORDER_CHANNEL: 'ORDER_CHANNEL',
  PRODUCT: 'PRODUCT',
  CATEGORY: 'CATEGORY',
  LOYALTY_TIER: 'LOYALTY_TIER',
  LOYALTY_POINTS: 'LOYALTY_POINTS',
  TAG: 'TAG',
  CONSENT: 'CONSENT',
  BIRTHDAY_MONTH: 'BIRTHDAY_MONTH',
  BIRTH_DATE_KNOWN: 'BIRTH_DATE_KNOWN',
} as const;
export type SegmentField = (typeof SegmentField)[keyof typeof SegmentField];

export const SegmentOperator = {
  GTE: 'GTE',
  LTE: 'LTE',
  GT: 'GT',
  LT: 'LT',
  EQ: 'EQ',
  NEQ: 'NEQ',
  IN: 'IN',
  NOT_IN: 'NOT_IN',
  IS_SET: 'IS_SET',
  IS_NOT_SET: 'IS_NOT_SET',
} as const;
export type SegmentOperator = (typeof SegmentOperator)[keyof typeof SegmentOperator];

/** What kind of value a field takes — decides which input the rule editor renders. */
export const SegmentValueKind = {
  number: 'number',
  money: 'money',
  idList: 'idList',
  enumList: 'enumList',
  monthList: 'monthList',
  consent: 'consent',
  presence: 'presence',
} as const;
export type SegmentValueKind = (typeof SegmentValueKind)[keyof typeof SegmentValueKind];

// ---------------------------------------------------------------------------
// §22 / ADR-0017 — fraud, loss prevention and anomaly detection.
// `src/modules/risk`. Rules before ML: every event names one rule, the window
// it measured, the count/money observed and the thresholds it was measured
// against — never a score.
// ---------------------------------------------------------------------------

export const RiskRuleKey = {
  VOID_RATE: 'VOID_RATE',
  REFUND_RATE: 'REFUND_RATE',
  MANUAL_DISCOUNT_REPETITION: 'MANUAL_DISCOUNT_REPETITION',
  PRICE_OVERRIDE_REPETITION: 'PRICE_OVERRIDE_REPETITION',
  DRAWER_VARIANCE: 'DRAWER_VARIANCE',
  STOCK_ADJUSTMENT_VALUE: 'STOCK_ADJUSTMENT_VALUE',
  AFTER_HOURS_ACTIVITY: 'AFTER_HOURS_ACTIVITY',
  MANAGER_OVERRIDE_CONCENTRATION: 'MANAGER_OVERRIDE_CONCENTRATION',
  SELF_APPROVAL: 'SELF_APPROVAL',
  COMPLIMENTARY_VALUE: 'COMPLIMENTARY_VALUE',
  LOYALTY_ADJUSTMENT: 'LOYALTY_ADJUSTMENT',
  PAYMENT_FAILURE_REPETITION: 'PAYMENT_FAILURE_REPETITION',
} as const;
export type RiskRuleKey = (typeof RiskRuleKey)[keyof typeof RiskRuleKey];

export const RiskSeverity = {
  LOW: 'LOW',
  MEDIUM: 'MEDIUM',
  HIGH: 'HIGH',
} as const;
export type RiskSeverity = (typeof RiskSeverity)[keyof typeof RiskSeverity];

/**
 * OPEN -> UNDER_REVIEW -> one of the three terminal outcomes. `FALSE_POSITIVE`
 * is first-class and never a flavour of `DISMISSED` — see ADR-0017's case
 * management section. `OPEN` is written only by the detection sweep; a person
 * resolving a case can never choose it as a target.
 */
export const RiskEventStatus = {
  OPEN: 'OPEN',
  UNDER_REVIEW: 'UNDER_REVIEW',
  CONFIRMED: 'CONFIRMED',
  FALSE_POSITIVE: 'FALSE_POSITIVE',
  DISMISSED: 'DISMISSED',
} as const;
export type RiskEventStatus = (typeof RiskEventStatus)[keyof typeof RiskEventStatus];

/** The four outcomes `ResolveRiskEventDto.status` actually accepts — `OPEN` excluded on purpose. */
export const RESOLVABLE_RISK_EVENT_STATUSES = [
  RiskEventStatus.UNDER_REVIEW,
  RiskEventStatus.CONFIRMED,
  RiskEventStatus.FALSE_POSITIVE,
  RiskEventStatus.DISMISSED,
] as const;

// ---------------------------------------------------------------------------
// §4 / ADR-0008 — employee identity, attendance and the time clock.
// `src/modules/employees`, `src/modules/attendance`, `src/modules/scheduling`.
// ---------------------------------------------------------------------------

export const EmploymentStatus = {
  PENDING: 'PENDING',
  ACTIVE: 'ACTIVE',
  SUSPENDED: 'SUSPENDED',
  TERMINATED: 'TERMINATED',
} as const;
export type EmploymentStatus = (typeof EmploymentStatus)[keyof typeof EmploymentStatus];

export const EmploymentType = {
  FULL_TIME: 'FULL_TIME',
  PART_TIME: 'PART_TIME',
  CASUAL: 'CASUAL',
  CONTRACT: 'CONTRACT',
  INTERN: 'INTERN',
} as const;
export type EmploymentType = (typeof EmploymentType)[keyof typeof EmploymentType];

export const EmployeeDocumentType = {
  ID_CARD: 'ID_CARD',
  PASSPORT: 'PASSPORT',
  RESIDENCE_PERMIT: 'RESIDENCE_PERMIT',
  WORK_PERMIT: 'WORK_PERMIT',
  HEALTH_CERTIFICATE: 'HEALTH_CERTIFICATE',
  FOOD_SAFETY_CERTIFICATE: 'FOOD_SAFETY_CERTIFICATE',
  CONTRACT: 'CONTRACT',
  CERTIFICATION: 'CERTIFICATION',
  OTHER: 'OTHER',
} as const;
export type EmployeeDocumentType = (typeof EmployeeDocumentType)[keyof typeof EmployeeDocumentType];

/** `EmployeeCompensation.payType` — reachable only through `compensation.view`/`.manage`. */
export const PayType = {
  HOURLY: 'HOURLY',
  MONTHLY: 'MONTHLY',
  DAILY: 'DAILY',
  PER_SHIFT: 'PER_SHIFT',
} as const;
export type PayType = (typeof PayType)[keyof typeof PayType];

export const TimePunchType = {
  CLOCK_IN: 'CLOCK_IN',
  CLOCK_OUT: 'CLOCK_OUT',
  BREAK_START: 'BREAK_START',
  BREAK_END: 'BREAK_END',
} as const;
export type TimePunchType = (typeof TimePunchType)[keyof typeof TimePunchType];

export const TimePunchSource = {
  TERMINAL: 'TERMINAL',
  WEB: 'WEB',
  /** Entered after the fact by someone holding `attendance.correct`. Never a rewrite of the original. */
  MANAGER_CORRECTION: 'MANAGER_CORRECTION',
  SYSTEM: 'SYSTEM',
} as const;
export type TimePunchSource = (typeof TimePunchSource)[keyof typeof TimePunchSource];

export const TimeEntryStatus = {
  OPEN: 'OPEN',
  PENDING_APPROVAL: 'PENDING_APPROVAL',
  APPROVED: 'APPROVED',
  REJECTED: 'REJECTED',
} as const;
export type TimeEntryStatus = (typeof TimeEntryStatus)[keyof typeof TimeEntryStatus];

export const AttendanceExceptionType = {
  LATE_ARRIVAL: 'LATE_ARRIVAL',
  EARLY_DEPARTURE: 'EARLY_DEPARTURE',
  MISSING_PUNCH: 'MISSING_PUNCH',
  OVERTIME: 'OVERTIME',
  UNSCHEDULED: 'UNSCHEDULED',
  NO_SHOW: 'NO_SHOW',
  OUT_OF_AREA: 'OUT_OF_AREA',
  BREAK_VIOLATION: 'BREAK_VIOLATION',
} as const;
export type AttendanceExceptionType =
  (typeof AttendanceExceptionType)[keyof typeof AttendanceExceptionType];

/** Off by default, and stays off unless a venue opts in — ADR-0008 §6, staff surveillance data. */
export const LocationCaptureMode = {
  OFF: 'OFF',
  ON_PUNCH: 'ON_PUNCH',
} as const;
export type LocationCaptureMode = (typeof LocationCaptureMode)[keyof typeof LocationCaptureMode];

export const GeofenceEnforcement = {
  OFF: 'OFF',
  WARN: 'WARN',
  BLOCK: 'BLOCK',
} as const;
export type GeofenceEnforcement = (typeof GeofenceEnforcement)[keyof typeof GeofenceEnforcement];

// ---------------------------------------------------------------------------
// §16 own delivery & driver operations (ADR-0029). `src/modules/delivery`.
// ---------------------------------------------------------------------------

/**
 * Deliberately its own machine, never folded into `OrderStatus`: a driver's
 * road state and an order's kitchen state are different questions that can
 * disagree at the same instant (food READY, driver still EN_ROUTE from the
 * delivery before this one).
 */
export const DeliveryStatus = {
  PENDING: 'PENDING',
  ASSIGNED: 'ASSIGNED',
  PICKED_UP: 'PICKED_UP',
  EN_ROUTE: 'EN_ROUTE',
  DELIVERED: 'DELIVERED',
  FAILED: 'FAILED',
  CANCELLED: 'CANCELLED',
} as const;
export type DeliveryStatus = (typeof DeliveryStatus)[keyof typeof DeliveryStatus];

export const DriverStatus = {
  ACTIVE: 'ACTIVE',
  INACTIVE: 'INACTIVE',
} as const;
export type DriverStatus = (typeof DriverStatus)[keyof typeof DriverStatus];

/** Feeds nothing computed yet; present so a future ETA/routing feature does not have to re-open this table. */
export const DriverVehicleType = {
  ON_FOOT: 'ON_FOOT',
  BICYCLE: 'BICYCLE',
  MOTORCYCLE: 'MOTORCYCLE',
  CAR: 'CAR',
  VAN: 'VAN',
} as const;
export type DriverVehicleType = (typeof DriverVehicleType)[keyof typeof DriverVehicleType];

export const DriverShiftStatus = {
  OPEN: 'OPEN',
  CLOSED: 'CLOSED',
} as const;
export type DriverShiftStatus = (typeof DriverShiftStatus)[keyof typeof DriverShiftStatus];

/** Metadata only — no file/photo upload exists behind `PHOTO`/`SIGNATURE` yet (ADR-0029 §2.8). */
export const ProofOfDeliveryType = {
  PHOTO: 'PHOTO',
  SIGNATURE: 'SIGNATURE',
  PIN: 'PIN',
  NONE: 'NONE',
} as const;
export type ProofOfDeliveryType = (typeof ProofOfDeliveryType)[keyof typeof ProofOfDeliveryType];

/** The explicit, required decision about food that came back from a FAILED delivery — ADR-0029 §2.7. */
export const DeliveryReturnDisposition = {
  RESTOCKED: 'RESTOCKED',
  DISCARDED: 'DISCARDED',
  DAMAGED: 'DAMAGED',
} as const;
export type DeliveryReturnDisposition =
  (typeof DeliveryReturnDisposition)[keyof typeof DeliveryReturnDisposition];

/** OFF is the only safe default — ADR-0029 §2.9, mirrors `LocationCaptureMode`'s reasoning for a driver's continuous position instead of a single punch stamp. */
export const DeliveryLocationCaptureMode = {
  OFF: 'OFF',
  ON_ACTIVE_DELIVERY: 'ON_ACTIVE_DELIVERY',
} as const;
export type DeliveryLocationCaptureMode =
  (typeof DeliveryLocationCaptureMode)[keyof typeof DeliveryLocationCaptureMode];

// ============================================================================
// ADR-0026 — messaging adapters and campaign automation.
// Pinned from `/private/tmp/hard-ops` (branch `docs/product-blueprint`, read
// against `Tarabeeza-Backend` `dev`), read-only source:
//   - src/modules/messaging/*.ts + messaging/dto/*.ts
//   - prisma/schema.prisma (NotificationChannel, CampaignStatus,
//     CampaignTriggerType, CampaignSendStatus)
// ============================================================================

/**
 * What a `Notification` travels over. Messaging (templates and campaigns)
 * only ever targets the two with a real transport and a consent channel to
 * check — `MESSAGING_CHANNELS` in `Pages/CRM/messaging/lib.ts` — but the full
 * five-member enum is mirrored here for schema fidelity, the same reasoning
 * every other enum in this file follows.
 */
export const NotificationChannel = {
  IN_APP: 'IN_APP',
  PUSH: 'PUSH',
  SMS: 'SMS',
  EMAIL: 'EMAIL',
  WEBSOCKET: 'WEBSOCKET',
} as const;
export type NotificationChannel = (typeof NotificationChannel)[keyof typeof NotificationChannel];

export const CampaignTriggerType = {
  ONE_OFF: 'ONE_OFF',
  WELCOME: 'WELCOME',
  WIN_BACK: 'WIN_BACK',
  POST_VISIT: 'POST_VISIT',
  BIRTHDAY: 'BIRTHDAY',
  LOYALTY_MILESTONE: 'LOYALTY_MILESTONE',
} as const;
export type CampaignTriggerType = (typeof CampaignTriggerType)[keyof typeof CampaignTriggerType];

export const CampaignStatus = {
  DRAFT: 'DRAFT',
  SCHEDULED: 'SCHEDULED',
  ACTIVE: 'ACTIVE',
  SENDING: 'SENDING',
  COMPLETED: 'COMPLETED',
  PAUSED: 'PAUSED',
  CANCELLED: 'CANCELLED',
} as const;
export type CampaignStatus = (typeof CampaignStatus)[keyof typeof CampaignStatus];

/** One customer's outcome on one campaign cycle — `campaigns.sends()`'s own ledger, never `@Audit`-ed (ADR-0026 §8). */
export const CampaignSendStatus = {
  SENT: 'SENT',
  SKIPPED_NO_CONSENT: 'SKIPPED_NO_CONSENT',
  SKIPPED_FATIGUE_CAP: 'SKIPPED_FATIGUE_CAP',
  SKIPPED_INVALID_RECIPIENT: 'SKIPPED_INVALID_RECIPIENT',
  FAILED: 'FAILED',
} as const;
export type CampaignSendStatus = (typeof CampaignSendStatus)[keyof typeof CampaignSendStatus];
